/**
 * People in mail (docs/features/mail.md, "People cards"; design system ContactCard): a sender's or recipient's name is
 * a `PersonName`; clicking it opens the contact card in a popover (Gmail's card on click). The card resolves the
 * address (core `resolvePerson`: workspace member, contact, or neither) and offers `personActions`: Message and View
 * profile for members; New email, Open contact, the Email / Client / Team switch, projects and Recent emails for
 * contacts; Add to contacts / Add as client for unknown senders; Copy email for everyone.
 * Native: the same card as a sheet (iOS medium detent, Android ModalBottomSheet).
 */
import type { ContactKind, MailAddress, MailContact, MailProject } from "@bundle/contracts";
import { Shell } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { Avatar, Button, Chip, Divider, Icon, Popover, Pressable, ScrollView, SegmentedControl, Stack, Text, TextField, useAnnouncer, type ChipTone } from "../primitives/index.ts";
import type { FontToken, IconName } from "@bundle/tokens";
import { useNavigation } from "../context/AppContext.tsx";
import { useMemberCard } from "../context/SettingsContext.tsx";
import { useFeatureAccess, useViewModel, useWorkspace } from "../hooks/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { openComposer, useMail, type MailServices } from "./registry.ts";

const KIND_TONE: Record<ContactKind, ChipTone> = { email: "neutral", client: "tag.blue", team: "tag.purple" };
const KIND_LABEL = { email: "mail.contacts.filter.email", client: "mail.people.kind.client", team: "mail.contacts.filter.team" } as const;

export type PersonNameProps = {
  address: MailAddress;
  /** What to show (default: the name in the header, else the address). */
  text?: string;
  variant?: FontToken;
  /** Semibold (a message's sender). */
  strong?: boolean;
  /** Secondary text colour (recipients, the address). */
  muted?: boolean;
  testID: string;
};

/** A name or address in mail that opens the person's contact card. Plain text outside a workspace with Email. */
export function PersonName({ address, text, variant = "body", strong, muted, testID }: PersonNameProps) {
  const t = useT();
  const mail = useMail();
  const [open, setOpen] = useState(false);
  const label = text ?? Mail.displayName(address);
  const cls = ["b-person", strong === true ? "b-person-strong" : "", muted === true ? "b-person-muted" : "", `b-person-${variant.replace(".", "-")}`].filter((x) => x !== "").join(" ");
  if (mail === undefined) {
    return (
      <Text variant={variant} weight={strong === true ? "semibold" : undefined} color={muted === true ? "text.secondary" : "text.primary"} lines={1}>
        {label}
      </Text>
    );
  }
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      side="bottom"
      align="start"
      width="card"
      anchor={
        <button
          type="button"
          className={cls}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t("mail.people.card", { name: Mail.displayName(address) })}
          data-testid={testID}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {label}
        </button>
      }
    >
      {open ? <ConnectedCard mail={mail} address={address} onClose={() => setOpen(false)} testID={`${testID}-card`} /> : null}
    </Popover>
  );
}

/** The card for an address, wired to the workspace (contacts, members, projects, navigation, the composer). */
function ConnectedCard({ mail, address, onClose, testID }: { mail: MailServices; address: MailAddress; onClose: () => void; testID: string }) {
  const ws = useWorkspace();
  const nav = useNavigation();
  const memberCard = useMemberCard();
  const featureAccess = useFeatureAccess();
  const access = featureAccess("mail");
  // Open contact goes with the Contacts area (its own switch).
  const contactsOn = featureAccess("contacts") !== "none";
  const contactsVm = mail.contacts;
  const cs = useViewModel(contactsVm);
  const mailboxId = useViewModel(mail.mailbox).mailboxId;
  useEffect(() => {
    contactsVm.ensureLoaded();
    if (cs.projectsStatus === "idle" || cs.projectsStatus === "failed") void contactsVm.loadProjects();
    // Once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsVm]);
  const cache = ws.state.cache;
  const members = useMemo(
    () => Object.values(cache?.members ?? {}).map((m) => ({ userId: m.userId, fullName: m.fullName, displayName: m.displayName, email: m.email, deactivated: m.deactivated })),
    [cache?.members],
  );
  const person = Mail.resolvePerson(address, cs.all, members);
  const contact = person.contactId === undefined ? undefined : cs.all.find((c) => c.id === person.contactId);
  const member = person.userId === undefined ? undefined : cache?.members[person.userId];
  const isMe = person.userId === ws.state.meId;
  const canSend = access === "full" && mailboxId !== undefined;
  const actions = Mail.personActions(person, { canChange: access === "full", canSend, isMe }).filter((a) => a !== "openContact" || contactsOn);
  const go = (url: string) => {
    onClose();
    nav.open(url);
  };
  const run = (a: Mail.PersonAction) => {
    switch (a) {
      case "message":
        if (person.userId !== undefined) go(Shell.formatUrl({ type: "dm", ws: ws.slug, userId: person.userId }));
        return;
      case "viewProfile":
        onClose();
        if (person.userId !== undefined) memberCard?.open(person.userId);
        return;
      case "newEmail":
        onClose();
        if (mailboxId !== undefined) openComposer({ mailboxId, mode: "new", to: [{ address: person.address, name: person.name }], cc: [], subject: "", text: "" });
        return;
      case "openContact":
        if (person.contactId !== undefined) go(Shell.formatUrl({ type: "contacts", ws: ws.slug, contactId: person.contactId }));
        return;
      case "recentEmails":
        go(Shell.formatUrl({ type: "mail", ws: ws.slug, q: Mail.mailWithSearch([person.address]) }));
        return;
      case "addContact":
      case "addAsClient":
        void contactsVm.add(person.address, address.name ?? "", a === "addAsClient" ? "client" : "email");
        return;
      default:
        return;
    }
  };
  return (
    <ContactCard
      person={person}
      contact={contact}
      avatarSrc={Mail.mailAvatarPath(ws.vm.wsId, person.address)}
      meta={member?.title}
      actions={actions}
      projects={cs.projects}
      projectsLoading={cs.projectsStatus === "loading"}
      onAction={run}
      onSetKind={(k) => (contact === undefined ? undefined : contactsVm.setContactKind(contact.id, k))}
      onToggleProject={(rowId, on) => (contact === undefined ? undefined : contactsVm.setProject(contact.id, rowId, on))}
      onOpenProject={(p) => go(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: p.rowId }))}
      testID={testID}
    />
  );
}

export type ContactCardProps = {
  person: Mail.MailPerson;
  contact?: MailContact | undefined;
  avatarSrc?: string | undefined;
  /** A member's title (members), shown under the address. */
  meta?: string | undefined;
  actions: Mail.PersonAction[];
  /** Every project (the card shows the contact's and picks from all). */
  projects: MailProject[];
  projectsLoading?: boolean;
  onAction: (a: Mail.PersonAction) => void;
  onSetKind: (kind: ContactKind) => void;
  onToggleProject: (rowId: string, on: boolean) => void;
  onOpenProject: (p: MailProject) => void;
  testID: string;
};

const ACTION_ICON: Record<Mail.PersonAction, IconName> = {
  message: "message",
  viewProfile: "user",
  newEmail: "mail",
  openContact: "user",
  addContact: "plus",
  addAsClient: "users",
  copyEmail: "copy",
  setKind: "tag",
  addToProject: "folder",
  recentEmails: "inbox",
};
const ACTION_LABEL = {
  message: "mail.people.message",
  viewProfile: "mail.people.viewProfile",
  newEmail: "mail.people.newEmail",
  openContact: "mail.people.openContact",
  addContact: "mail.people.addContact",
  addAsClient: "mail.people.addClient",
  copyEmail: "mail.people.copyEmail",
  setKind: "mail.contacts.kind",
  addToProject: "mail.people.addToProject",
  recentEmails: "mail.people.recent",
} as const;

/** The card itself (no data access): header, chips, the kind switch and projects, two buttons, then more rows. */
export function ContactCard({ person, contact, avatarSrc, meta, actions, projects, projectsLoading, onAction, onSetKind, onToggleProject, onOpenProject, testID }: ContactCardProps) {
  const t = useT();
  const announce = useAnnouncer();
  const copy = () => {
    void navigator.clipboard?.writeText(person.address).then(
      () => announce(t("mail.people.copied")),
      () => undefined,
    );
  };
  const mine = contact === undefined ? [] : Mail.contactProjects(contact.id, projects);
  // The two buttons, then the rest as rows; the copy, kind and project controls live inline.
  const inline = new Set<Mail.PersonAction>(["copyEmail", "setKind", "addToProject"]);
  const main = actions.filter((a) => !inline.has(a));
  const buttons = main.slice(0, 2);
  const rows = main.slice(2);
  const detail = meta ?? (contact === undefined ? undefined : [contact.org, contact.messageCount > 0 ? t("mail.contacts.messages", { count: contact.messageCount }) : undefined].filter((x) => x !== undefined && x !== "").join(" · "));
  const kind = person.contactKind;
  return (
    <Stack testID={testID} accessibilityLabel={t("mail.people.card", { name: person.name })}>
      <Stack direction="horizontal" align="start" gap="3" padding="4">
        <Avatar name={person.name} src={avatarSrc} size="avatar.lg" />
        <Stack grow gap="0.5">
          <Text variant="headline" lines={2} selectable testID={`${testID}-name`}>
            {person.name}
          </Text>
          <Stack direction="horizontal" align="center" gap="0.5">
            <Stack grow>
              <Text variant="footnote" color="text.secondary" lines={1} selectable>
                {person.address}
              </Text>
            </Stack>
            {actions.includes("copyEmail") ? <IconAction icon="copy" label={t("mail.people.copyEmail")} onPress={copy} testID={`${testID}-copy`} /> : null}
          </Stack>
          {detail !== undefined && detail !== "" ? (
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {detail}
            </Text>
          ) : null}
          <Stack direction="horizontal" align="center" gap="1" wrap>
            {kind !== undefined && kind !== "email" ? <Chip label={t(KIND_LABEL[kind])} tone={KIND_TONE[kind]} /> : null}
            {person.kind === "member" ? <Chip label={t("mail.contacts.member")} /> : null}
            {person.kind === "unknown" ? <Chip label={t("mail.people.notContact")} /> : null}
          </Stack>
        </Stack>
      </Stack>
      {actions.includes("setKind") || actions.includes("addToProject") || mine.length > 0 ? (
        <>
          <Divider />
          <Stack gap="2.5" paddingX="4" paddingY="3">
            {actions.includes("setKind") && contact !== undefined ? (
              <Stack direction="horizontal" align="center" justify="between" gap="2">
                <Text variant="caption" color="text.secondary">
                  {t("mail.contacts.kind")}
                </Text>
                <SegmentedControl<ContactKind>
                  size="sm"
                  value={contact.kind}
                  onChange={onSetKind}
                  accessibilityLabel={t("mail.contacts.kind")}
                  options={[
                    { value: "email", label: t("mail.contacts.filter.email") },
                    { value: "client", label: t("mail.people.kind.client") },
                    { value: "team", label: t("mail.contacts.filter.team") },
                  ]}
                  testID={`${testID}-kind`}
                />
              </Stack>
            ) : null}
            <Text variant="caption" color="text.secondary">
              {t("mail.people.projects")}
            </Text>
            <Stack direction="horizontal" align="center" gap="1" wrap>
              {mine.map((p) => (
                <Chip key={p.rowId} label={projectLabel(p, t("db.untitled"))} onPress={() => onOpenProject(p)} testID={`${testID}-project-${p.rowId}`} />
              ))}
              {actions.includes("addToProject") && contact !== undefined ? (
                <ProjectPicker contactName={person.name} contactId={contact.id} projects={projects} loading={projectsLoading === true} onToggle={onToggleProject} testID={`${testID}-add-project`} />
              ) : null}
            </Stack>
          </Stack>
        </>
      ) : null}
      {buttons.length > 0 ? (
        <>
          <Divider />
          <Stack direction="horizontal" align="center" gap="2" paddingX="4" paddingY="3" wrap>
            {buttons.map((a, i) => (
              <Button key={a} size="sm" variant={i === 0 ? "primary" : "secondary"} icon={ACTION_ICON[a]} label={t(ACTION_LABEL[a])} onPress={() => onAction(a)} testID={`${testID}-${a}`} />
            ))}
          </Stack>
        </>
      ) : null}
      {rows.length > 0 ? (
        <Stack paddingX="2" paddingY="1">
          {rows.map((a) => (
            <Pressable key={a} direction="horizontal" radius="sm" onPress={() => onAction(a)} testID={`${testID}-${a}`}>
              <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
                <Icon name={ACTION_ICON[a]} size="icon.sm" color="text.secondary" />
                <Text variant="footnote">{a === "recentEmails" ? t("mail.people.recentWith", { name: firstName(person.name) }) : t(ACTION_LABEL[a])}</Text>
              </Stack>
            </Pressable>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

function IconAction({ icon, label, onPress, testID }: { icon: IconName; label: string; onPress: () => void; testID: string }) {
  return (
    <Pressable direction="horizontal" radius="sm" onPress={onPress} accessibilityLabel={label} tooltip={label} testID={testID}>
      <Stack padding="1">
        <Icon name={icon} size="icon.sm" color="text.secondary" />
      </Stack>
    </Pressable>
  );
}

function firstName(name: string): string {
  return name.includes("@") ? name : (name.split(/\s+/)[0] ?? name);
}

export function projectLabel(p: Pick<MailProject, "title" | "icon">, untitled: string): string {
  const title = p.title.trim() === "" ? untitled : p.title;
  return p.icon === undefined || p.icon.startsWith("http") || p.icon.length > 8 ? title : `${p.icon} ${title}`;
}

/** "Add to project": the projects to pick from (done ones last); a tick marks hers, picking one again takes her out. */
export function ProjectPicker({ contactName, contactId, projects, loading, onToggle, testID }: { contactName: string; contactId: string; projects: MailProject[]; loading: boolean; onToggle: (rowId: string, on: boolean) => void; testID: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = projects.filter((p) => p.canEdit && (q === "" || p.title.toLowerCase().includes(q)));
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      side="bottom"
      align="start"
      width="picker"
      anchor={<Chip label={t("mail.people.addToProject")} icon="plus" onPress={() => setOpen(true)} testID={testID} />}
    >
      <Stack gap="1.5" padding="2" accessibilityLabel={t("mail.people.addToProjectTitle", { name: contactName })} testID={`${testID}-picker`}>
        <TextField value={query} onChange={setQuery} placeholder={t("mail.people.findProject")} accessibilityLabel={t("mail.people.findProject")} leadingIcon="search" autoFocus testID={`${testID}-search`} />
        <ScrollView>
          {shown.length === 0 ? (
            <Stack padding="2">
              <Text variant="footnote" color="text.tertiary">
                {loading ? "…" : t("mail.people.noProjects")}
              </Text>
            </Stack>
          ) : (
            shown.map((p) => {
              const on = p.contactIds.includes(contactId);
              return (
                <Pressable key={p.rowId} direction="horizontal" radius="sm" selected={on} onPress={() => onToggle(p.rowId, !on)} accessibilityLabel={on ? t("mail.people.removeFromProject", { project: p.title }) : p.title} testID={`${testID}-row-${p.rowId}`}>
                  <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                    <Stack grow>
                      <Text variant="footnote" lines={1}>
                        {projectLabel(p, t("db.untitled"))}
                      </Text>
                    </Stack>
                    {on ? <Icon name="check" size="icon.sm" color="accent.text" /> : p.status === undefined ? null : (
                      <Text variant="caption" color="text.tertiary" lines={1}>
                        {p.status.name}
                      </Text>
                    )}
                  </Stack>
                </Pressable>
              );
            })
          )}
        </ScrollView>
      </Stack>
    </Popover>
  );
}

const NO_CONTACTS: MailContact[] = [];
const NO_PROJECTS: MailProject[] = [];
const noSubscribe = () => () => {};

/** The project of a conversation from this sender (core `threadProject`), loading contacts and projects once. */
export function useThreadProject(from: MailAddress | undefined): MailProject | undefined {
  const vm = useMail()?.contacts;
  const all = useSyncExternalStore(vm?.subscribe ?? noSubscribe, () => vm?.getSnapshot().all ?? NO_CONTACTS);
  const projects = useSyncExternalStore(vm?.subscribe ?? noSubscribe, () => vm?.getSnapshot().projects ?? NO_PROJECTS);
  useEffect(() => {
    if (vm === undefined) return;
    vm.ensureLoaded();
    if (vm.state.projectsStatus === "idle") void vm.loadProjects();
  }, [vm]);
  return from === undefined ? undefined : Mail.threadProject(from, all, projects);
}

/** The project chip on a conversation: opens the project (`still`: a plain chip, inside a row that opens the thread). */
export function ThreadProjectChip({ from, still, testID }: { from: MailAddress | undefined; still?: boolean; testID: string }): ReactNode {
  const t = useT();
  const ws = useWorkspace();
  const nav = useNavigation();
  const p = useThreadProject(from);
  if (p === undefined) return null;
  const icon = p.icon === undefined ? { icon: "folder" as const } : {};
  if (still === true) return <Chip label={projectLabel(p, t("db.untitled"))} {...icon} />;
  return <Chip label={projectLabel(p, t("db.untitled"))} {...icon} onPress={() => nav.open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: p.rowId }))} testID={testID} />;
}
